/**
 * Assessment Storage
 *
 * Manages persistence for assessment blueprints, attempts, and reports.
 * Delegates to dataLayer for all storage operations (localStorage / future backend).
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 * dataLayer migration: D3 (8 april 2026).
 */

import {
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentReport,
  AssessmentHistoryEntry,
  AssessmentDomainId,
  CooldownResult,
} from '../types'
import { GroupId } from '../../config/systemConfig'
import { ASSESSMENT_COOLDOWN } from '../../config/constants'
import { dataLayer } from '../../data/dataLayer'
import { debug, error as logError } from '../../utils/logger'

// ── Blueprints ──────────────────────────────────────────────────────────────

/**
 * F-AS-R1-STORAGE-QUOTA (May 2026): returns `true` on successful persistence,
 * `false` if the underlying storage layer rejected the write (quota exceeded
 * after eviction, or a non-quota exception). Callers MUST handle a `false`
 * return — the typical pattern is to abort the start/complete flow and
 * surface an `AssessmentErrorModal` so the learner does not see a silent
 * data-loss success UI.
 */
export async function saveBlueprint(
  blueprint: AssessmentBlueprint,
): Promise<boolean> {
  try {
    const ok = await dataLayer.saveAssessmentBlueprint(blueprint.id, blueprint)
    if (ok) {
      debug(`[AssessmentStorage] Saved blueprint: ${blueprint.id}`)
    } else {
      logError(
        `[AssessmentStorage] Persistence failed for blueprint ${blueprint.id} (storage quota or eviction failure)`,
      )
    }
    return ok
  } catch (error) {
    logError('Failed to save blueprint:', error)
    return false
  }
}

export async function getBlueprint(
  id: string,
): Promise<AssessmentBlueprint | null> {
  try {
    const result =
      await dataLayer.getAssessmentBlueprint<AssessmentBlueprint>(id)
    return result ?? null
  } catch (error) {
    logError('Failed to load blueprint:', error)
    return null
  }
}

export async function getAllBlueprints(): Promise<AssessmentBlueprint[]> {
  try {
    const all =
      await dataLayer.getAllAssessmentBlueprints<AssessmentBlueprint>()
    return Object.values(all).sort((a, b) => b.createdAt - a.createdAt)
  } catch (error) {
    logError('Failed to load blueprints:', error)
    return []
  }
}

// ── Attempts ────────────────────────────────────────────────────────────────

/** See `saveBlueprint` docstring for the boolean contract. */
export async function saveAttempt(
  attempt: AssessmentAttempt,
): Promise<boolean> {
  try {
    const ok = await dataLayer.saveAssessmentAttempt(attempt.id, attempt)
    if (ok) {
      debug(`[AssessmentStorage] Saved attempt: ${attempt.id}`)
    } else {
      logError(
        `[AssessmentStorage] Persistence failed for attempt ${attempt.id} (storage quota or eviction failure)`,
      )
    }
    return ok
  } catch (error) {
    logError('Failed to save attempt:', error)
    return false
  }
}

export async function getAttempt(
  id: string,
): Promise<AssessmentAttempt | null> {
  try {
    const result = await dataLayer.getAssessmentAttempt<AssessmentAttempt>(id)
    return result ?? null
  } catch (error) {
    logError('Failed to load attempt:', error)
    return null
  }
}

export async function getAllAttempts(): Promise<AssessmentAttempt[]> {
  try {
    const all = await dataLayer.getAllAssessmentAttempts<AssessmentAttempt>()
    return Object.values(all).sort((a, b) => b.startedAt - a.startedAt)
  } catch (error) {
    logError('Failed to load attempts:', error)
    return []
  }
}

export async function getAttemptsByGroup(
  group: GroupId,
): Promise<AssessmentAttempt[]> {
  const all = await getAllAttempts()
  return all.filter((a) => a.selectedGroup === group)
}

// ── Learner scoping (AS-2026-07-P1-01) ──────────────────────────────────────

/**
 * AS-2026-07-P1-01 (July 2026) — LEGACY ATTEMPT POLICY.
 *
 * Attempts created before `learnerId` existed on `AssessmentAttempt` (V4)
 * cannot be attributed to anyone. We treat them as **unattributed**, which
 * means they are excluded from every learner-scoped read:
 *
 *  - Excluded from a learner's history → we never show learner A a result
 *    that might belong to learner B (privacy first).
 *  - Excluded from a learner's cooldown → we never block learner A because
 *    of a test that might belong to learner B.
 *
 * The rule is deliberately the same in both directions: an attempt we cannot
 * attribute is attributed to nobody. Unattributed attempts remain visible in
 * the group-wide teacher views, which is where they are still useful.
 */
export function isAttemptOwnedByLearner(
  attempt: AssessmentAttempt,
  learnerId: string,
): boolean {
  return (
    typeof attempt.learnerId === 'string' && attempt.learnerId === learnerId
  )
}

/**
 * All attempts belonging to ONE learner, optionally narrowed to a group.
 * This is the only correct source for learner-facing assessment surfaces.
 */
export async function getAttemptsForLearner(
  learnerId: string,
  group?: GroupId,
): Promise<AssessmentAttempt[]> {
  const scoped = group ? await getAttemptsByGroup(group) : await getAllAttempts()
  return scoped.filter((a) => isAttemptOwnedByLearner(a, learnerId))
}

/**
 * Wis alle toetsgegevens van ÉÉN leerling van dit apparaat.
 *
 * Nodig voor "afmelden op dit apparaat". Bewust learner-scoped en niet
 * `clearAllAssessmentData()`: op een gedeeld gezinsapparaat zou dat ook de
 * toetsgeschiedenis van een broertje of zusje wissen — iemand anders zijn
 * gegevens verwijderen omdat jíj je afmeldt is nooit het bedoelde gedrag.
 *
 * Toetspogingen zonder `learnerId` (van vóór V4) blijven staan: die zijn aan
 * niemand toe te schrijven, dus ook niet aan degene die zich afmeldt.
 *
 * Retourneert hoeveel pogingen zijn gewist — die telling gaat naar de
 * bevestiging, zodat "gewist" geen loze mededeling is.
 */
export async function deleteAssessmentDataForLearner(
  learnerId: string,
): Promise<number> {
  const attempts = await getAttemptsForLearner(learnerId)
  for (const attempt of attempts) {
    try {
      await dataLayer.deleteAssessmentAttempt(attempt.id)
    } catch (error) {
      logError('Failed to delete attempt for learner:', error)
    }
  }
  return attempts.length
}

// ── Reports ─────────────────────────────────────────────────────────────────

/** See `saveBlueprint` docstring for the boolean contract. */
export async function saveReport(report: AssessmentReport): Promise<boolean> {
  try {
    const ok = await dataLayer.saveAssessmentReport(report.attemptId, report)
    if (ok) {
      debug(`[AssessmentStorage] Saved report: ${report.attemptId}`)
    } else {
      logError(
        `[AssessmentStorage] Persistence failed for report ${report.attemptId} (storage quota or eviction failure)`,
      )
    }
    return ok
  } catch (error) {
    logError('Failed to save report:', error)
    return false
  }
}

export async function getReport(
  attemptId: string,
): Promise<AssessmentReport | null> {
  try {
    const result =
      await dataLayer.getAssessmentReport<AssessmentReport>(attemptId)
    return result ?? null
  } catch (error) {
    logError('Failed to load report:', error)
    return null
  }
}

export async function getAllReports(): Promise<AssessmentReport[]> {
  try {
    const all = await dataLayer.getAllAssessmentReports<AssessmentReport>()
    return Object.values(all).sort((a, b) => b.completedAt - a.completedAt)
  } catch (error) {
    logError('Failed to load reports:', error)
    return []
  }
}

/*
 AS-2026-07-P3-03 (July 2026): `getReportsByGroup` was removed. It was
 exported from the assessment barrel but had zero consumers, so it was an
 unreviewed group-scoped read path sitting next to the learner-scoping bug
 this round fixed. Reports are always fetched per `attemptId` from a
 learner-scoped history (see `getAssessmentHistoryForLearner`); if a
 group-wide report list is ever genuinely needed, add it back deliberately
 with an explicit learner-scope decision at the call-site.
 */

// ── History & Cooldown ──────────────────────────────────────────────────────

/** Shared attempt → history-entry projection. */
function toHistoryEntry(a: AssessmentAttempt): AssessmentHistoryEntry {
  const correctCount = a.answers.filter((ans) => ans.isCorrect).length
  const percentage =
    a.answers.length > 0
      ? Math.round((correctCount / a.answers.length) * 100)
      : 0
  return {
    attemptId: a.id,
    blueprintId: a.blueprintId,
    completedAt: a.completedAt ?? Date.now(),
    selectedGroup: a.selectedGroup,
    score: percentage,
    totalQuestions: a.answers.length,
    totalTimeSeconds: a.totalTimeSeconds,
    domainId: a.domainId,
    assignmentId: a.assignmentId,
    learnerId: a.learnerId,
  }
}

function isCompleted(a: AssessmentAttempt): boolean {
  return a.status === 'completed' && a.completedAt !== null
}

/**
 * GROUP-WIDE history — contains EVERY learner in the group.
 *
 * ⚠️ AS-2026-07-P1-01: do NOT use this for a learner-facing surface, and do
 * not use it to answer "what has learner X done". Use
 * `getAssessmentHistoryForLearner` instead, which enforces the scope in one
 * place instead of relying on each call-site remembering to filter.
 *
 * Legitimate consumers are the teacher class views, which intentionally show
 * all learners.
 */
export async function getAssessmentHistory(
  group?: GroupId,
): Promise<AssessmentHistoryEntry[]> {
  const attempts = group
    ? await getAttemptsByGroup(group)
    : await getAllAttempts()
  return attempts
    .filter(isCompleted)
    .map(toHistoryEntry)
    .sort((a, b) => b.completedAt - a.completedAt)
}

/**
 * Completed-assessment history for ONE learner (AS-2026-07-P1-01).
 *
 * This is the canonical accessor for every learner-facing surface and for
 * any portal view that claims to show a specific learner's results.
 * See `isAttemptOwnedByLearner` for the legacy-attempt policy.
 */
export async function getAssessmentHistoryForLearner(
  learnerId: string,
  group?: GroupId,
): Promise<AssessmentHistoryEntry[]> {
  const attempts = await getAttemptsForLearner(learnerId, group)
  return attempts
    .filter(isCompleted)
    .map(toHistoryEntry)
    .sort((a, b) => b.completedAt - a.completedAt)
}

// ── Delete & Clear ──────────────────────────────────────────────────────────

export async function deleteAttempt(id: string): Promise<void> {
  try {
    await dataLayer.deleteAssessmentAttempt(id)
    debug(`[AssessmentStorage] Deleted attempt and report: ${id}`)
  } catch (error) {
    logError('Failed to delete attempt:', error)
  }
}

export async function clearAllAssessmentData(): Promise<void> {
  try {
    await dataLayer.clearAllAssessmentData()
    debug('[AssessmentStorage] Cleared all assessment data')
  } catch (error) {
    logError('Failed to clear assessment data:', error)
  }
}

// ── Cooldown ────────────────────────────────────────────────────────────────

const NO_COOLDOWN: CooldownResult = {
  canStart: true,
  remainingMs: 0,
  lastCompletedAt: 0,
}

/** Build a CooldownResult from the most recent qualifying completion. */
function cooldownFrom(attempts: AssessmentAttempt[]): CooldownResult {
  if (attempts.length === 0) return { ...NO_COOLDOWN }
  const lastCompleted = Math.max(...attempts.map((a) => a.completedAt ?? 0))
  if (lastCompleted === 0) return { ...NO_COOLDOWN }
  const remaining =
    ASSESSMENT_COOLDOWN.MIN_INTERVAL_MS - (Date.now() - lastCompleted)
  return {
    canStart: remaining <= 0,
    remainingMs: Math.max(0, remaining),
    lastCompletedAt: lastCompleted,
  }
}

/** Does this completed attempt count towards `domainId`'s cooldown? */
function countsForDomainCooldown(
  a: AssessmentAttempt,
  domainId: AssessmentDomainId,
): boolean {
  // Match this domain OR legacy attempts without domainId (conservative).
  return a.domainId === domainId || !a.domainId
}

/**
 * Timestamp of the last completed assessment in a group — ACROSS ALL LEARNERS.
 *
 * ⚠️ AS-2026-07-P1-01: not suitable for gating a specific learner. Use
 * `getLastAssessmentTimestampForLearner`.
 */
export async function getLastAssessmentTimestamp(
  group: GroupId,
): Promise<number> {
  const attempts = (await getAttemptsByGroup(group)).filter(isCompleted)
  if (attempts.length === 0) return 0
  return Math.max(...attempts.map((a) => a.completedAt ?? 0))
}

/** Timestamp of ONE learner's last completed assessment in a group. */
export async function getLastAssessmentTimestampForLearner(
  learnerId: string,
  group: GroupId,
): Promise<number> {
  const attempts = (await getAttemptsForLearner(learnerId, group)).filter(
    isCompleted,
  )
  if (attempts.length === 0) return 0
  return Math.max(...attempts.map((a) => a.completedAt ?? 0))
}

/**
 * Group-wide cooldown check.
 *
 * ⚠️ AS-2026-07-P1-01: this blocks on ANY learner's attempt in the group.
 * Use `checkAssessmentCooldownForLearner` for anything learner-facing.
 */
export async function checkAssessmentCooldown(
  group: GroupId,
): Promise<CooldownResult> {
  const attempts = (await getAttemptsByGroup(group)).filter(isCompleted)
  return cooldownFrom(attempts)
}

/** Cooldown for ONE learner (all domains). */
export async function checkAssessmentCooldownForLearner(
  learnerId: string,
  group: GroupId,
): Promise<CooldownResult> {
  const attempts = (await getAttemptsForLearner(learnerId, group)).filter(
    isCompleted,
  )
  return cooldownFrom(attempts)
}

/**
 * Group-wide per-domain cooldown.
 *
 * ⚠️ AS-2026-07-P1-01: blocks on ANY learner's attempt in the group. Use
 * `checkDomainCooldownForLearner` for anything learner-facing.
 */
export async function checkDomainCooldown(
  group: GroupId,
  domainId: AssessmentDomainId,
): Promise<CooldownResult> {
  const attempts = (await getAttemptsByGroup(group)).filter(
    (a) => isCompleted(a) && countsForDomainCooldown(a, domainId),
  )
  return cooldownFrom(attempts)
}

/**
 * Per-domain cooldown for ONE learner (AS-2026-07-P1-01).
 *
 * This is the accessor the learner-facing assessment page must use: a
 * classmate finishing a Rekenen toets on the same device must never put this
 * learner on cooldown.
 */
export async function checkDomainCooldownForLearner(
  learnerId: string,
  group: GroupId,
  domainId: AssessmentDomainId,
): Promise<CooldownResult> {
  const attempts = (await getAttemptsForLearner(learnerId, group)).filter(
    (a) => isCompleted(a) && countsForDomainCooldown(a, domainId),
  )
  return cooldownFrom(attempts)
}
